import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench,
  Users,
  Heart,
  ChevronRight,
  Check,
  FileDigit,
  School,
  Clock,
  Map,
  Leaf,
  ArrowRight,
} from 'lucide-react';

const MAIN_SERVICES = [
  {
    icon: School,
    title: 'Burial Services',
    description:
      'Traditional burial services with dignity and respect, including ground burial, mausoleum entombment, and memorial planning.',
    features: [
      'Ground burial options',
      'Mausoleum entombment',
      'Monument and marker selection',
      'Family estates',
      'Green burial options',
    ],
  },
  {
    icon: Leaf,
    title: 'Cremation Services',
    description:
      'Complete cremation services with various memorial options to create meaningful tributes and lasting remembrances.',
    features: [
      'Traditional cremation',
      'Witness cremation',
      'Cremation memorialization',
      'Columbarium niches',
      'Scattering gardens',
    ],
  },
];

const ADDITIONAL_SERVICES = [
  {
    icon: FileDigit,
    title: 'Cemetery Records Management',
    description:
      'Digital transformation and maintenance of cemetery records for efficient management and preservation.',
    link: '/contact',
  },
  {
    icon: Map,
    title: 'Cemetery Planning',
    description:
      'Expert consultation for cemetery development, expansion, and optimization of space utilization.',
    link: '/contact',
  },
  {
    icon: Clock,
    title: 'Perpetual Care',
    description:
      'Long-term maintenance and care services ensuring the lasting beauty and dignity of memorial spaces.',
    link: '/contact',
  },
];

const WHY_US = [
  {
    icon: Heart,
    title: 'Compassionate Care',
    body: 'Our experienced team provides empathetic support and guidance throughout your journey, ensuring your needs are met with understanding and respect.',
  },
  {
    icon: Users,
    title: 'Family-Focused Approach',
    body: 'We understand the importance of family and work closely with you to create meaningful tributes that honor your loved ones’ legacy.',
  },
  {
    icon: Wrench,
    title: 'Professional Excellence',
    body: 'We maintain the highest standards in cemetery management and memorial services through continuous improvement and innovation.',
  },
];

const Services = () => (
  <div className="bg-white">
    {/* Hero */}
    <div className="relative h-[440px]">
      <img
        src="/images/services/banner.jpg"
        alt="A wooded path winding through the grounds"
        className="absolute h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-950/70 to-navy-950/80" />
      <div className="container relative mx-auto flex h-full flex-col items-center justify-center px-4 text-center">
        <p className="eyebrow animate-fade-up text-navy-100">What we offer</p>
        <h1 className="mt-4 animate-fade-up text-4xl text-white md:text-5xl">Our Services</h1>
        <p className="mt-5 max-w-2xl animate-fade-up font-montserrat leading-relaxed text-slate-200 md:text-lg">
          Comprehensive memorial and cemetery services delivered with compassion, dignity, and
          excellence.
        </p>
      </div>
    </div>

    {/* Core services */}
    <section className="border-b border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow">For families</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Core Memorial Services</h2>
          <div className="rule mt-5" />
        </div>
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-2">
          {MAIN_SERVICES.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className="flex flex-col rounded-2xl border border-slate-200 bg-white p-8 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
              >
                <span className="icon-chip">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-2xl">{service.title}</h3>
                <p className="mt-3 font-montserrat text-sm leading-relaxed text-slate-600">
                  {service.description}
                </p>
                <ul className="mt-6 space-y-3 border-t border-slate-100 pt-6">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-navy-600" />
                      <span className="font-montserrat text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Cemetery management services */}
    <section className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <p className="eyebrow">For cemeteries</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Cemetery Management Services</h2>
          <div className="rule mt-5" />
        </div>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {ADDITIONAL_SERVICES.map((service) => {
            const Icon = service.icon;
            return (
              <div
                key={service.title}
                className="group flex flex-col rounded-2xl border border-slate-200 bg-slate-50/70 p-8 transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:bg-white hover:shadow-card-hover"
              >
                <div className="mb-4 flex items-center gap-3">
                  <span className="icon-chip h-11 w-11">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="text-xl">{service.title}</h3>
                </div>
                <p className="font-montserrat text-sm leading-relaxed text-slate-600">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="mt-auto inline-flex w-fit items-center gap-1 pt-6 font-montserrat text-sm font-medium text-navy-700 transition-all duration-300 hover:gap-2 hover:text-navy-900"
                >
                  Learn More
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Why choose us */}
    <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-24">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-14 max-w-3xl text-center">
            <p className="eyebrow">The difference</p>
            <h2 className="mt-3 text-3xl md:text-4xl">Why Choose Cemetery &amp; Burial Solutions</h2>
            <p className="mt-4 font-montserrat leading-relaxed text-slate-600">
              Our commitment to excellence, compassionate care, and innovative solutions sets us
              apart in providing comprehensive memorial and cemetery services.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            {WHY_US.map(({ icon: Icon, title, body }) => (
              <div key={title} className="text-center md:text-left">
                <span className="icon-chip mx-auto md:mx-0">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-xl">{title}</h3>
                <p className="mt-2 font-montserrat text-sm leading-relaxed text-slate-600">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Call to action */}
    <section className="relative overflow-hidden bg-navy-gradient py-24 text-center text-white">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(50% 60% at 50% 0%, rgba(78,143,233,0.35) 0%, rgba(8,45,104,0) 70%)',
        }}
      />
      <div className="container relative mx-auto px-4">
        <div className="mx-auto max-w-3xl">
          <p className="eyebrow text-navy-200">Let’s talk</p>
          <h2 className="mt-3 text-3xl text-white md:text-4xl">Ready to Learn More?</h2>
          <p className="mx-auto mt-4 max-w-2xl font-montserrat leading-relaxed text-navy-100">
            Contact us to discuss your needs or schedule a consultation with our experienced team.
          </p>
          <Link to="/contact" className="btn-on-dark mt-8">
            Contact Us Today
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Services;
